import { apiClient } from "@/lib/api";
import { fetchAllPages } from "@/lib/fetchAllPages";
import type {
  ActivityCategory,
  ActivityItem,
  ActivityPaymentModel,
  ActivitySalesAgentAssignmentDetail,
  SalesAgentCreatePayload,
  SalesAgentRead,
  SalesAgentScopedAssignmentsResponse,
  Translations,
  UserRole,
} from "@/types";

/** Backend caps: GET /admin/activities and /admin/houses `limit` le=500. */
const ADMIN_ACTIVITIES_PAGE_SIZE = 500;
const ADMIN_HOUSES_PAGE_SIZE = 500;
/** Backend cap: GET /admin/sellers `limit` le=100. */
const ADMIN_SELLERS_PAGE_SIZE = 100;
/** Backend caps: GET /admin/{sellers,hosts}/{id}/activities `limit` le=100. */
const OWNER_ACTIVITIES_PAGE_SIZE = 100;

// ========== Types ==========

export interface CatalogImportDraftItem {
  name: string;
  description: string;
  price_cents: number;
  category_label?: string | null;
}

export interface CatalogImportPreviewResponse {
  activity_language: string;
  detected_source_language: string | null;
  was_translated_to_activity_language: boolean;
  items: CatalogImportDraftItem[];
}

export interface PlatformStats {
  /** Integer cents when ``stats:read``; otherwise null. */
  total_wallet_balance: number | null;
  total_sellers: number | null;
  active_sellers: number | null;
  total_activities: number | null;
  active_activities: number | null;
  total_restaurants: number | null;
  total_services: number | null;
  total_houses: number | null;
  active_houses: number | null;
}

export interface SellerWithStats {
  user_id: string; // Primary key (references users.id)
  name: string;
  account_email: string;
  contact_phone: string | null;
  is_active: boolean;
  /** Integer cents. */
  wallet_balance: number;
  activities_count: number;
  created_at: string;
}

export interface HostDetail {
  user_id: string;
  name: string;
  account_email: string;
  contact_phone: string | null;
  contact_email: string | null;
  vat_number: string | null;
  billing_address: string | null;
  is_active: boolean;
  /** Integer cents. */
  wallet_balance: number;
  /** Integer cents. */
  total_revenue: number;
  structures_count: number;
  last_login_at: string | null;
  password_reset_email: StaffPasswordResetEmailStatus;
  created_at: string;
  updated_at: string;
}

export interface SalesAgentDetailAdmin extends SalesAgentRead {
  /** Integer cents. */
  wallet_balance: number;
  /** Integer cents. */
  total_revenue: number;
  assigned_scopes_count: number;
  contact_phone: string | null;
  contact_email: string | null;
  vat_number: string | null;
  last_login_at: string | null;
  password_reset_email: StaffPasswordResetEmailStatus;
}

export interface StaffPasswordResetEmailStatus {
  can_send: boolean;
  blocked_reason: "ACTIVE_LINK" | "COOLDOWN" | null;
  active_link_expires_at: string | null;
  next_allowed_at: string | null;
}

export interface SellerDetail {
  user_id: string; // Primary key (references users.id)
  name: string;
  account_email: string;
  contact_phone: string | null;
  contact_email: string | null;
  vat_number: string | null;
  billing_address: string | null;
  is_active: boolean;
  /** Integer cents. */
  wallet_balance: number;
  /** Integer cents. */
  total_revenue: number;
  last_login_at: string | null;
  password_reset_email: StaffPasswordResetEmailStatus;
  created_at: string;
  updated_at: string;
}

export interface ActivityWithConfig {
  id: string;
  seller_id: string;
  category: string;
  payment_model: string;
  name_translations: Translations;
  description_translations: Translations;
  is_active: boolean;
  is_visible: boolean;
  commission_rate: number; // 0.0-1.0
  /** Whole euros (not cents) — matches backend Activity field. */
  avg_price_per_person: number | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityConfigUpdate {
  commission_rate?: number; // 0.0-1.0
  category?: string;
  is_active?: boolean;
}

export interface AdminUserListRow {
  user_id: string;
  role: "SELLER" | "HOST" | "SALES_AGENT" | "ADMIN";
  name: string;
  account_email: string;
  contact_phone: string | null;
  is_active: boolean;
  wallet_balance: number;
  relevant_count: number;
  region_code: string | null;
  /** True/false for ADMIN rows; null for other roles. Always present from the API. */
  is_super_admin: boolean | null;
}

export interface AdminActivityListRow {
  id: string;
  name_translations: Translations;
  category: ActivityCategory;
  payment_model: ActivityPaymentModel;
  is_active: boolean;
  is_visible: boolean;
  owner_user_id: string;
  owner_name: string;
  owner_role: UserRole;
  commission_rate: number;
  has_active_discount: boolean;
  region_code: string;
  created_at: string;
}

export type PromoMarginMode = "ABSOLUTE" | "PERCENTAGE";

export interface ActivityPromoRead {
  id: string;
  activity_id: string;
  activity_item_id: string | null;
  margin_mode: PromoMarginMode;
  margin_value: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromoStructureRatesRead {
  host_rate: number;
  sales_agent_activity_rate: number;
  sales_agent_house_rate: number;
}

export interface PromoPreviewContextItem {
  id: string;
  /** English name, shown when the UI-language translation is missing. */
  name: string;
  name_translations: Translations;
  price_cents: number;
  commission_rate: number;
}

export interface PromoPreviewContext {
  processor_rate: number;
  structure_rates: PromoStructureRatesRead[];
  items: PromoPreviewContextItem[];
}

export interface PromoPreviewSummary {
  min_discount_cents: number;
  median_discount_cents: number;
  max_discount_cents: number;
  max_margin_cents: number;
}

export interface PromoPreviewItemOverride {
  activity_item_id: string;
  margin_mode: PromoMarginMode;
  margin_value: number;
}

export interface PromoPreviewRequest {
  margin_mode: PromoMarginMode;
  margin_value: number;
  item_overrides?: PromoPreviewItemOverride[];
}

export interface PromoPreviewItemResult {
  activity_item_id: string;
  preview: PromoPreviewSummary;
}

export interface PromoPreviewResponse {
  items: PromoPreviewItemResult[];
}

export interface AdminHouseListRow {
  id: string;
  name: string;
  address: string | null;
  is_active: boolean;
  owner_user_id: string;
  owner_name: string;
  commission_rate: number;
  region_code: string | null;
  created_at: string;
}

export const adminService = {
  // Get platform statistics
  getStats: async (): Promise<PlatformStats> => {
    const response = await apiClient.get<PlatformStats>("/admin/stats");
    return response.data;
  },

  getUsers: async (params?: {
    role?: "SELLER" | "HOST" | "SALES_AGENT" | "ADMIN";
    region_id?: string;
  }): Promise<AdminUserListRow[]> => {
    const response = await apiClient.get<AdminUserListRow[]>("/admin/users", {
      params,
    });
    return response.data;
  },

  getUserRole: async (
    userId: string,
  ): Promise<{ user_id: string; role: UserRole }> => {
    const response = await apiClient.get<{ user_id: string; role: UserRole }>(
      `/admin/users/${userId}`,
    );
    return response.data;
  },

  getActivities: (filters?: {
    is_active?: boolean;
    is_visible?: boolean;
    category?: string;
    search?: string;
    region_id?: string;
  }): Promise<AdminActivityListRow[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<AdminActivityListRow[]>(
        "/admin/activities",
        { params: { ...filters, ...page } },
      );
      return response.data;
    }, ADMIN_ACTIVITIES_PAGE_SIZE),

  listActivityPromos: async (
    activityId: string,
  ): Promise<ActivityPromoRead[]> => {
    const response = await apiClient.get<ActivityPromoRead[]>(
      `/activity-promos/${activityId}`,
    );
    return response.data;
  },

  getActivityPromoPreviewContext: async (
    activityId: string,
  ): Promise<PromoPreviewContext> => {
    const response = await apiClient.get<PromoPreviewContext>(
      `/activity-promos/${activityId}/preview-context`,
    );
    return response.data;
  },

  previewActivityPromos: async (
    activityId: string,
    body: PromoPreviewRequest,
  ): Promise<PromoPreviewResponse> => {
    const response = await apiClient.post<PromoPreviewResponse>(
      `/activity-promos/${activityId}/preview`,
      body,
    );
    return response.data;
  },

  upsertActivityPromo: async (params: {
    activityId: string;
    activity_item_id?: string | null;
    margin_mode: PromoMarginMode;
    margin_value: number;
    is_active: boolean;
  }): Promise<ActivityPromoRead> => {
    const response = await apiClient.put<ActivityPromoRead>(
      `/activity-promos/${params.activityId}`,
      {
        activity_item_id: params.activity_item_id ?? null,
        margin_mode: params.margin_mode,
        margin_value: params.margin_value,
        is_active: params.is_active,
      },
    );
    return response.data;
  },

  deleteActivityPromo: async (
    activityId: string,
    promoId: string,
  ): Promise<void> => {
    await apiClient.delete(`/activity-promos/${activityId}/${promoId}`);
  },

  extractCatalogFromPdf: async (
    activityId: string,
    file: File,
  ): Promise<CatalogImportPreviewResponse> => {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post<CatalogImportPreviewResponse>(
      `/activities/${activityId}/catalog-import/extract`,
      form,
    );
    return response.data;
  },

  confirmCatalogImport: async (
    activityId: string,
    items: CatalogImportDraftItem[],
    labels: string[] = [],
  ): Promise<ActivityItem[]> => {
    const response = await apiClient.post<ActivityItem[]>(
      `/activities/${activityId}/catalog-import/confirm`,
      { items, labels },
    );
    return response.data;
  },

  getHouses: (filters?: {
    is_active?: boolean;
    search?: string;
    region_id?: string;
  }): Promise<AdminHouseListRow[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<AdminHouseListRow[]>(
        "/admin/houses",
        { params: { ...filters, ...page } },
      );
      return response.data;
    }, ADMIN_HOUSES_PAGE_SIZE),

  getHost: async (hostId: string): Promise<HostDetail> => {
    const response = await apiClient.get<HostDetail>(`/admin/hosts/${hostId}`);
    return response.data;
  },

  // List sellers with stats
  getSellers: (filters?: { search?: string }): Promise<SellerWithStats[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<SellerWithStats[]>(
        "/admin/sellers",
        { params: { ...filters, ...page } },
      );
      return response.data;
    }, ADMIN_SELLERS_PAGE_SIZE),

  // Get seller detail
  getSeller: async (sellerId: string): Promise<SellerDetail> => {
    const response = await apiClient.get<SellerDetail>(
      `/admin/sellers/${sellerId}`,
    );
    return response.data;
  },

  // Get seller activities
  getSellerActivities: (sellerId: string): Promise<ActivityWithConfig[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<ActivityWithConfig[]>(
        `/admin/sellers/${sellerId}/activities`,
        { params: page },
      );
      return response.data;
    }, OWNER_ACTIVITIES_PAGE_SIZE),

  getHostActivities: (hostId: string): Promise<ActivityWithConfig[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<ActivityWithConfig[]>(
        `/admin/hosts/${hostId}/activities`,
        { params: page },
      );
      return response.data;
    }, OWNER_ACTIVITIES_PAGE_SIZE),

  // Update activity config
  updateActivityConfig: async (
    activityId: string,
    config: ActivityConfigUpdate,
  ): Promise<void> => {
    await apiClient.patch(`/admin/activities/${activityId}/config`, config);
  },

  sendSellerPasswordResetEmail: async (
    sellerId: string,
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/admin/sellers/${sellerId}/password-reset-email`,
    );
    return response.data;
  },

  sendHostPasswordResetEmail: async (
    hostId: string,
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/admin/hosts/${hostId}/password-reset-email`,
    );
    return response.data;
  },

  sendSalesAgentPasswordResetEmail: async (
    userId: string,
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/sales-agents/${userId}/password-reset-email`,
    );
    return response.data;
  },

  getSalesAgent: async (userId: string): Promise<SalesAgentDetailAdmin> => {
    const response = await apiClient.get<SalesAgentDetailAdmin>(
      `/sales-agents/${userId}`,
    );
    return response.data;
  },

  updateSalesAgent: async (
    userId: string,
    data: { is_active?: boolean },
  ): Promise<SalesAgentDetailAdmin> => {
    const response = await apiClient.patch<SalesAgentDetailAdmin>(
      `/sales-agents/${userId}`,
      data,
    );
    return response.data;
  },

  getSalesAgentScopedAssignments: async (
    salesAgentUserId: string,
  ): Promise<SalesAgentScopedAssignmentsResponse> => {
    const response = await apiClient.get<SalesAgentScopedAssignmentsResponse>(
      `/sales-agents/${salesAgentUserId}/scoped-assignments`,
    );
    return response.data;
  },

  getActivitySalesAgentAssignment: async (
    activityId: string,
  ): Promise<ActivitySalesAgentAssignmentDetail | null> => {
    const response = await apiClient.get<
      ActivitySalesAgentAssignmentDetail | null
    >(`/sales-agents/activity-assignments/${activityId}`);
    return response.data;
  },

  createSalesAgent: async (
    data: SalesAgentCreatePayload,
  ): Promise<SalesAgentRead> => {
    const response = await apiClient.post<SalesAgentRead>(
      "/sales-agents",
      data,
    );
    return response.data;
  },

  upsertSalesAgentActivityAssignment: async (body: {
    activity_id: string;
    sales_agent_user_id: string;
    commission_rate: number;
  }): Promise<{
    activity_id: string;
    sales_agent_user_id: string;
    commission_rate: number;
  }> => {
    const response = await apiClient.put<{
      activity_id: string;
      sales_agent_user_id: string;
      commission_rate: number;
    }>("/sales-agents/activity-assignments", body);
    return response.data;
  },

  deleteSalesAgentActivityAssignment: async (
    activityId: string,
  ): Promise<void> => {
    await apiClient.delete(`/sales-agents/activity-assignments/${activityId}`);
  },

  upsertSalesAgentHouseAssignment: async (body: {
    house_id: string;
    sales_agent_user_id: string;
    commission_rate: number;
  }): Promise<{
    house_id: string;
    sales_agent_user_id: string;
    commission_rate: number;
  }> => {
    const response = await apiClient.put<{
      house_id: string;
      sales_agent_user_id: string;
      commission_rate: number;
    }>("/sales-agents/house-assignments", body);
    return response.data;
  },

  deleteSalesAgentHouseAssignment: async (houseId: string): Promise<void> => {
    await apiClient.delete(`/sales-agents/house-assignments/${houseId}`);
  },
};
